/** biome-ignore-all lint/style/useNamingConvention: @todo */

// note: all of this is very whacky because of mapping to an e-commerce template

import { type Cart, type OrderItem, PRICES } from "@dransay/api/models";
import { createLogger } from "@dransay/logger/browser";

const logger = createLogger({ service: "platform" });

import type { BaseTrackingEvent } from "~/tooling/telemetry/events";
import { gtmDataLayer } from "./get-gtm-data-layer";

// note: page visits are tracked separately by a script
export type EventName =
	| "register"
	| "log_in"
	| "add_to_cart"
	| "remove_from_cart"
	| "begin_checkout"
	| "purchase";

// gtm specific item format
export interface GtmCartItem {
	item_name: string; // Product name

	item_id: string; // Unique product ID (AnsayID)
	price: number | "unknown"; // Unit price of the product in decimal format (euros)
	quantity: number; // Quantity in cart

	item_brand: string; // Vendor name

	item_category?: string | null; // Cannabis category
}

interface GtmCartItemAction {
	itemId: string;
	cart: Cart;
	// note: this isn't actually calculated, we don't know the price until checkout
	// but the template requires it
	prescriptionValue: number; // expected in cents, will be converted to decimal for GTM
}

export type EventParamsMap = {
	register: undefined;

	log_in: undefined;

	// e-commerce events

	add_to_cart: GtmCartItemAction;

	remove_from_cart: GtmCartItemAction;

	begin_checkout: {
		cart: Cart;
		// note: this isn't actually calculated, we don't know the price until checkout
		// but the template requires it
		prescriptionValue: number; // expected in cents, will be converted to decimal for GTM
	};

	purchase: {
		orderId: string;
		prescriptionCost: number; // expected in cents, will be converted to decimal for GTM
		vendorCost: number; // expected in cents, will be converted to decimal for GTM
		items: OrderItem[];
		vendor?: {
			name?: string | null;
		};
		voucherCode?: string;
	};
};

export function captureEvent(event: BaseTrackingEvent | null) {
	if (typeof window === "undefined" || event === null) {
		return;
	}

	gtmDataLayer().push(event);
}

function pushGtmEvent<T extends EventName>(
	eventName: T,
	params?: EventParamsMap[T],
): void {
	if (typeof window === "undefined") {
		return;
	}
	gtmDataLayer().push(formatEventForGTM(eventName, params));
}

export function trackBeginCheckout(
	params: EventParamsMap["begin_checkout"],
): void {
	pushGtmEvent("begin_checkout", params);
}

/**
 * type-safe event tracking function
 * uses overloading for param handling
 * @param eventName the name of the event to track
 * @param params parameters for the event (type checked based on eventName)
 * @deprecated We will migrate to rudderstack events do not add new ones, use track()
 */
// for events with required params
export function trackEvent<T extends EventName>(
	eventName: T,
	...params: EventParamsMap[T] extends undefined ? [] : [EventParamsMap[T]]
): void;

/**
 * @deprecated We will migrate to rudderstack events do not add new ones, use track()
 */
export function trackEvent<T extends EventName>(
	eventName: T,
	...params: EventParamsMap[T] extends undefined ? [] : [EventParamsMap[T]]
): void {
	pushGtmEvent(eventName, params[0]);
}

/**
 * converts cents to decimal euros with 2 decimal places
 * @param cents price in cents
 * @returns formatted price in euros (e.g., "14.99")
 */
function centsToDecimal(
	cents: number | null | undefined | "unknown",
): number | "unknown" {
	if (cents === null || cents === undefined || cents === "unknown") {
		return "unknown";
	}
	return Number((cents / 100).toFixed(2));
}

// converts a cart item to GTM format
function mapCartItemToGtm(
	item: Cart["items"][number],
	cart: Cart,
): GtmCartItem {
	return {
		item_name: item.product.name,

		item_id: item.id,
		// price is expected in cents, convert to decimal for GTM
		price: centsToDecimal(item.product.price),
		quantity: item.quantity,

		item_brand: cart.vendor?.name ?? "PHARMACY",

		item_category: item.product.category,
	};
}

// helper to get all cart items in the required format
function getCartItems(cart: Cart): GtmCartItem[] {
	return cart.items.map((item) => mapCartItemToGtm(item, cart));
}

// formats events according to the GTM requirements
function formatEventForGTM<T extends EventName>(
	eventName: T,
	params?: EventParamsMap[T],
): Record<string, unknown> {
	// base event structure
	const baseEvent = { event: eventName, service_environment: "CANNABIS" };

	switch (eventName) {
		// handle simple events
		case "register":
		case "log_in":
			return baseEvent;

		// handle cart events
		case "add_to_cart":
		case "remove_from_cart": {
			const { itemId, cart, prescriptionValue } = params as EventParamsMap[
				| "add_to_cart"
				| "remove_from_cart"];

			// Find the item in the cart by ID
			const cartItem = cart.items.find((item) => item.id === itemId);

			if (!cartItem) {
				logger.warn({ itemId, eventName }, "Item not found in cart for event");
				return baseEvent;
			}

			const gtmItem = mapCartItemToGtm(cartItem, cart);

			return {
				...baseEvent,

				event_label: "cannabis",
				ecommerce: {
					currency: "EUR",
					// prescriptionValue is in cents, convert to decimal for GTM
					value: centsToDecimal(prescriptionValue),
					items: [gtmItem],
				},
			};
		}

		case "begin_checkout": {
			const { cart, prescriptionValue } =
				params as EventParamsMap["begin_checkout"];
			const cartItems = getCartItems(cart);

			// prescription item (always first in the array)
			// also a template requirement
			const prescriptionItem: GtmCartItem = {
				item_name: "cannabis",

				item_id: "cannabis",
				// price in cents, convert to decimal for GTM
				price: centsToDecimal(PRICES.onlineService),
				quantity: 1,

				item_brand: "Dr. Ansay",

				item_category: "cannabis",
			};

			return {
				...baseEvent,

				event_label: "cannabis",
				ecommerce: {
					currency: "EUR",
					// prescriptionValue is in cents, convert to decimal for GTM
					value: centsToDecimal(prescriptionValue),
					items: [prescriptionItem, ...cartItems],
				},
			};
		}

		case "purchase": {
			const {
				orderId,
				prescriptionCost,
				vendorCost,
				items,
				vendor,
				voucherCode,
			} = params as EventParamsMap["purchase"];

			const gtmItems = items.map((item) => ({
				item_name: item.name,

				item_id: item.id,
				// item.price is expected in cents, convert to decimal for GTM
				price: centsToDecimal(item.price),
				quantity: item.quantity,

				item_brand: vendor?.name ?? "PHARMACY",

				item_category: item.category,
			}));

			// prescription item (always first in the array)
			// also a template requirement
			const prescriptionItem: GtmCartItem = {
				item_name: "cannabis",

				item_id: "cannabis",
				// prescriptionCost is in cents, convert to decimal for GTM
				price: centsToDecimal(prescriptionCost),
				quantity: 1,

				item_brand: "Dr. Ansay",

				item_category: "cannabis",
			};

			return {
				...baseEvent,

				event_label: "cannabis",

				journey_started: "web", // Always 'web' since it can be 'web', 'ios', or 'android'
				ecommerce: {
					transaction_id: orderId,
					// prescriptionCost is in cents, convert to decimal for GTM
					value: centsToDecimal(prescriptionCost), // Only Dr. Ansay's services
					// vendorCost is in cents, convert to decimal for GTM
					shipping: centsToDecimal(vendorCost), // Combined price of products in the basket
					coupon: voucherCode, // Optional: Applied coupon code
					currency: "EUR",
					items: [prescriptionItem, ...gtmItems],
				},
			};
		}

		default:
			return { ...baseEvent, ...(params ?? {}) };
	}
}
